import { useEffect, useId } from "react";
import type { Atom } from "jotai";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { AppStore } from "@utils/jotai";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AiContextType =
  | "selected-metric"
  | "selected-task"
  | "selected-alert"
  | "selected-row"
  | "error-message"
  | "incident"
  | "highlighted-text"
  | "custom";

export interface AiContextItem {
  /** Unique identifier for React keys and removal */
  id: string;
  /** Context type - determines the predefined description */
  type: AiContextType;
  /** The actual value to send to AI */
  value: string;
  /** Optional label override (defaults to predefined description) */
  label?: string;
  /** Component/page source (optional, for debugging) */
  source?: string;
}

// ---------------------------------------------------------------------------
// Predefined Descriptions
// ---------------------------------------------------------------------------

/**
 * Predefined descriptions for each context type.
 * These are shown to the AI to help it understand what the value refers to.
 */
export const AI_CONTEXT_DESCRIPTIONS: Record<AiContextType, string> = {
  "selected-metric": "Currently selected metric in the metrics table",
  "selected-task": "Currently selected task",
  "selected-alert": "Currently selected alert",
  "selected-row": "Currently selected row",
  "error-message": "Error message from the application",
  incident: "Active incident details",
  "highlighted-text": "Text highlighted by the user",
  custom: "Additional context",
};

// ---------------------------------------------------------------------------
// Atom
// ---------------------------------------------------------------------------

export const aiContextItemsAtom = atom<AiContextItem[]>([]);
aiContextItemsAtom.debugLabel = "aiContextItemsAtom";

// ---------------------------------------------------------------------------
// Hooks for setting context
// ---------------------------------------------------------------------------

/**
 * Hook for components to add context that auto-clears on unmount.
 * Use this for useState-derived or ephemeral context.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   useAiContextOnMount({
 *     type: 'selected-row',
 *     value: `Task: ${task.name}, Status: ${task.status}`,
 *   });
 *   return <div>...</div>;
 * };
 * ```
 */
export const useAiContextOnMount = (
  item: Omit<AiContextItem, "id"> | null | undefined
) => {
  const id = useId();
  const setItems = useSetAtom(aiContextItemsAtom);

  useEffect(() => {
    if (!item) return;

    const contextItem: AiContextItem = { ...item, id };
    setItems((prev) => [...prev.filter((i) => i.id !== id), contextItem]);

    return () => {
      setItems((prev) => prev.filter((i) => i.id !== id));
    };
  }, [id, item?.type, item?.value, item?.label, item?.source, setItems]);
};

/**
 * Returns a setter function to imperatively add/update context.
 * The returned function adds or updates context by ID.
 *
 * @example
 * ```tsx
 * const setContext = useSetAiContext();
 * // Add context
 * setContext({ id: 'my-context', type: 'custom', value: 'Some value' });
 * // Remove context
 * setContext({ id: 'my-context', type: 'custom', value: '' }); // empty value removes
 * ```
 */
export const useSetAiContext = () => {
  const setItems = useSetAtom(aiContextItemsAtom);

  return (item: AiContextItem) => {
    if (!item.value) {
      // Empty value = remove
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } else {
      setItems((prev) => [...prev.filter((i) => i.id !== item.id), item]);
    }
  };
};

/**
 * Returns a function to remove context by ID.
 */
export const useRemoveAiContext = () => {
  const setItems = useSetAtom(aiContextItemsAtom);
  return (id: string) => setItems((prev) => prev.filter((i) => i.id !== id));
};

/**
 * Clears all AI context items.
 */
export const useClearAiContext = () => {
  const setItems = useSetAtom(aiContextItemsAtom);
  return () => setItems([]);
};

/**
 * Hook to read current AI context items.
 */
export const useAiContextItems = () => useAtomValue(aiContextItemsAtom);

/**
 * Hook for syncing AI context from an external atom.
 * Use this when context should follow an atom's value - the atom author controls lifecycle.
 * Context is automatically added/removed based on transform returning a value or null.
 *
 * @param sourceAtom - The atom to derive context from
 * @param transform - Function to transform atom value into AI context (return null to clear)
 *
 * @example
 * ```tsx
 * // When selection comes from URL/search state, derive a memoized item and pass to useAiContextOnMount:
 * const aiContextItem = useMemo(() => {
 *   if (!rowSelectionModel.ids.size) return null;
 *   const rowId = Array.from(rowSelectionModel.ids)[0];
 *   return {
 *     type: 'selected-row' as const,
 *     value: `Row ID: ${rowId}`,
 *     label: 'Selected Metric Row',
 *     source: 'MetricsTable',
 *   };
 * }, [rowSelectionModel]);
 * useAiContextOnMount(aiContextItem);
 * ```
 */
export const useAiContextFromAtom = <T>(
  sourceAtom: Atom<T>,
  transform: (value: T) => Omit<AiContextItem, "id"> | null
) => {
  const atomValue = useAtomValue(sourceAtom);
  const id = useId();
  const setItems = useSetAtom(aiContextItemsAtom);

  useEffect(() => {
    const contextItem = transform(atomValue);

    if (contextItem === null) {
      // Transform returned null - remove context
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      // Add/update context
      setItems((prev) => [
        ...prev.filter((i) => i.id !== id),
        { ...contextItem, id },
      ]);
    }
  }, [atomValue, id, setItems, transform]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setItems((prev) => prev.filter((i) => i.id !== id));
    };
  }, [id, setItems]);
};

// ---------------------------------------------------------------------------
// Non-hook utilities (for atom subscriptions in toolbox)
// ---------------------------------------------------------------------------

/**
 * Set AI context imperatively (outside React components).
 * Use sparingly - prefer hooks when possible.
 */
export const setAiContextItem = (item: AiContextItem) => {
  if (!item.value) {
    AppStore.set(aiContextItemsAtom, (prev) => prev.filter((i) => i.id !== item.id));
  } else {
    AppStore.set(aiContextItemsAtom, (prev) => [
      ...prev.filter((i) => i.id !== item.id),
      item,
    ]);
  }
};

/**
 * Remove AI context item by ID imperatively.
 */
export const removeAiContextItem = (id: string) => {
  AppStore.set(aiContextItemsAtom, (prev) => prev.filter((i) => i.id !== id));
};

/**
 * Clear all AI context items imperatively.
 */
export const clearAiContext = () => {
  AppStore.set(aiContextItemsAtom, []);
};

// ---------------------------------------------------------------------------
// Context formatting for prompts
// ---------------------------------------------------------------------------

/**
 * Formats context items into a string for the AI prompt.
 * Each item includes its description and value.
 */
export const formatAiContextForPrompt = (items: AiContextItem[]): string => {
  if (!items.length) return "";

  return items
    .map((item) => {
      const description = item.label ?? AI_CONTEXT_DESCRIPTIONS[item.type];
      const formattedValue = item.value.replace(/, /g, "\n");
      return `${description}\n\n${formattedValue}`;
    })
    .join("\n");
};
